<template>
  <div class="py-4">
    <div class="mb-8 flex" :class="{ 'gap-3': isFixed }">
      <Input
        v-model="minInput"
        :class="{
          'w-[110px]': isFixed,
          'w-1/2 pr-1.5': !isFixed,
        }"
        name="min"
        :label="t['w.module.filter_bar.min']()"
        @keyup.enter="commit"
        @change="commit"
      />
      <Input
        v-model="maxInput"
        :class="{
          'w-[110px]': isFixed,
          'w-1/2 pl-1.5': !isFixed,
        }"
        name="max"
        :label="t['w.module.filter_bar.max']()"
        @keyup.enter="commit"
        @change="commit"
      />
    </div>

    <div v-if="min !== max" class="px-2">
      <SliderRoot v-model="sliderModel" class="relative flex h-[18px] touch-none select-none items-center" :min="min" :max="max" :step="interval" @value-commit="commit">
        <SliderTrack class="relative h-0.5 grow rounded-full bg-background-dark">
          <SliderRange class="absolute h-full rounded-full bg-icon-active" />
        </SliderTrack>
        <SliderThumb class="block size-4 rounded-full bg-icon-active focus:outline-none" />
        <SliderThumb class="block size-4 rounded-full bg-icon-active focus:outline-none" />
      </SliderRoot>
    </div>
  </div>
</template>

<script setup lang="ts">
// Known bug, slider thumbs are not centered to slider track edges, causing mouse cursor offsetting at extreme values. https://github.com/radix-ui/primitives/issues/1966
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui'

import * as t from '~/messages'

import Input from '~/components/input/Input.vue'

interface Range {
  min: number
  max: number
}

const props = withDefaults(
  defineProps<{
    title: string
    group: number
    min: number
    max: number
    interval: number
    isFixed?: boolean
    appliedFilters: Range
  }>(),
  {
    isFixed: true,
  }
)

const minInput = ref(props.appliedFilters.min)
const maxInput = ref(props.appliedFilters.max)

const sliderModel = computed<[number, number]>({
  get() {
    return [minInput.value, maxInput.value]
  },
  set(value) {
    minInput.value = value[0]
    maxInput.value = value[1]
  },
})

const clamp = (value: number, min: number, max: number, defaultValue: number) => {
  if (isNaN(value)) {
    return defaultValue
  }

  if (value < min) {
    return min
  }

  if (value > max) {
    return max
  }

  return value
}

const sorted = (a: number, b: number): [number, number] => {
  return a < b ? [a, b] : [b, a]
}

const commit = () => {
  const [min, max] = sorted(clamp(minInput.value, props.min, props.max, props.min), clamp(maxInput.value, props.min, props.max, props.max))

  minInput.value = min
  maxInput.value = max

  if (props.appliedFilters.min !== min || props.appliedFilters.max !== max) {
    emit('updateRange', props.group, min, max)
  }
}

watch(
  () => props.appliedFilters,
  (newValue) => {
    minInput.value = newValue.min
    maxInput.value = newValue.max
  }
)

const emit = defineEmits<{
  updateRange: [group: number, min: number, max: number]
}>()
</script>
